import { useEffect, useMemo, useState } from 'react';
import { getLifecareStructuredFromResultRequests } from '@demandproai/shared';
import useCaseResultsHistory from './useCaseResultsHistory';
import {
  buildEmptyPlan,
  getClientDisplayName,
  normalizePlanForForm,
} from '../utils/lifecarePlanUtils';

const getLatestSnapshotClients = (resultRequests) => {
  for (let index = (resultRequests?.length || 0) - 1; index >= 0; index -= 1) {
    const snapshotClients = resultRequests[index]?.snapshot?.caseData?.clients;
    if (Array.isArray(snapshotClients) && snapshotClients.length) return snapshotClients;
  }
  return null;
};

// A client is editable in the lifecare studio if ANY of these are true:
//   1. typed lifecarePlanStructured already exists (new flow)
//   2. legacy markdown lifecarePlan text exists (pre-typed-shape generation)
//   3. a rendered lifecare PDF s3 path exists (generation ran previously)
//   4. lifecare planning is enabled on the client (toggle on, generation may still be pending)
// For 2/3/4 we seed an empty typed plan so the user can start filling it in.
const hasLifecareEligibility = (client) => {
  const postAccidentResult = client?.medicalProviders?.postAccident?.result;
  console.log("client post accident ", postAccidentResult);
  if (postAccidentResult?.lifecarePlanStructured) return true;
  if (postAccidentResult?.lifecarePlan) return true;
  if (client?.result?.lifecarePlan?.s3FilePath) return true;
  if (client?.damage?.isProcessLifeCarePlan === true) return true;
  return false;
};

const transformLifecareClientToIdDisplayName = ({ client, index, hasSnapshotPlan }) => {
  console.log("Client to lifecare display ", client);
  if (!client?.clientId) return null;
  if (!hasSnapshotPlan && !hasLifecareEligibility(client)) return null;
  return {
    clientId: client.clientId,
    clientDisplayName: getClientDisplayName({ client, index }),
  };
};

// seed the form from the typed plan when present; otherwise return a blank
// structured plan pre-filled with whatever client info we already have on the case
const buildInitialFormValuesForClient = ({ client, plan }) => {
  if (plan) return normalizePlanForForm(plan);
  return buildEmptyPlan({
    clientName: client?.personalInfo?.clientName,
    currentAge: client?.personalInfo?.age,
    dateOfIncident: client?.caseInfo?.dateOfIncident,
  });
};

const useLifecareStudioData = ({ caseId, isOpen }) => {
  const {
    caseData: liveCaseData,
    createRequests: resultRequests,
    fetchCaseDetails,
    hasMoreHistory,
    loadOlderUntil,
  } = useCaseResultsHistory({ caseId });
  const [isInitialLoadPending, setIsInitialLoadPending] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let isCurrent = true;
    setIsInitialLoadPending(true);
    Promise.resolve(fetchCaseDetails()).finally(() => {
      if (isCurrent) setIsInitialLoadPending(false);
    });
    return () => {
      isCurrent = false;
    };
  }, [isOpen, fetchCaseDetails]);

  const caseDetails = useMemo(() => {
    if (!liveCaseData) return null;
    const snapshotClients = getLatestSnapshotClients(resultRequests);
    return snapshotClients?.length ? { ...liveCaseData, clients: snapshotClients } : liveCaseData;
  }, [liveCaseData, resultRequests]);

  const clients = useMemo(() => caseDetails?.clients || [], [caseDetails]);
  const lifecareClientIdAndDisplayNames = useMemo(
    () => clients
      .map((client, index) => transformLifecareClientToIdDisplayName({
        client,
        index,
        hasSnapshotPlan: Boolean(getLifecareStructuredFromResultRequests(resultRequests, client?.clientId)),
      }))
      .filter(Boolean),
    [clients, resultRequests]
  );

  useEffect(() => {
    if (!lifecareClientIdAndDisplayNames.length) {
      setSelectedClientId('');
      return;
    }
    const hasSelected = lifecareClientIdAndDisplayNames.some((option) => option.clientId === selectedClientId);
    if (hasSelected) return;
    setSelectedClientId(lifecareClientIdAndDisplayNames[0].clientId);
  }, [lifecareClientIdAndDisplayNames, selectedClientId]);

  const selectedClient = useMemo(
    () => clients.find((client) => client?.clientId === selectedClientId) || null,
    [clients, selectedClientId]
  );

  const selectedPlan = useMemo(() => {
    if (!selectedClientId) return null;
    return getLifecareStructuredFromResultRequests(resultRequests, selectedClientId);
  }, [resultRequests, selectedClientId]);

  const initialFormValues = useMemo(
    () => buildInitialFormValuesForClient({ client: selectedClient, plan: selectedPlan }),
    [selectedClient, selectedPlan]
  );

  const isNewPlanForSelectedClient = Boolean(selectedClientId) && !selectedPlan;

  const caseName = caseDetails?.caseInfo?.caseName || 'Lifecare Plan';

  return {
    caseDetails,
    caseName,
    clients,
    isInitialLoadPending,
    lifecareClientIdAndDisplayNames,
    selectedClientId,
    setSelectedClientId,
    selectedClient,
    selectedPlan,
    initialFormValues,
    isNewPlanForSelectedClient,
    resultRequests,
    hasMoreHistory,
    loadOlderUntil,
    refetchCaseDetails: fetchCaseDetails,
  };
};

export default useLifecareStudioData;
